import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";

// Constants
import { GOOGLE_API } from "@/Constants/api";

const API_URL = `${GOOGLE_API.place}:autocomplete`;
const API_KEY = import.meta.env.VITE_APP_GOOGLE_MAPS_API_KEY;
const ONE_MINUTE = 1000 * 60; // 1 minute in milliseconds

function predictionToSuggestion(prediction: PlacePrediction): AutocompleteSuggestion {
	return {
		placeId: prediction.placeId,
		searchableText: prediction.text.text,
	};
}

export function usePlacesAutocomplete({ input, debounceMs = 300, enabled = true, bias = [] }: UsePlacesAutocompleteOptions) {
	const inputText = !input ? "" : [input, ...bias].join(" ");
	const [debouncedInput, setDebouncedInput] = useState(inputText);

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedInput(inputText), debounceMs);
		return () => clearTimeout(timer);
	}, [inputText, debounceMs]);

	const queryEnabled = enabled && debouncedInput.trim().length > 0;

	const query = useQuery<AutocompleteResponse, Error>({
		queryKey: ["places-autocomplete", debouncedInput],
		queryFn: async () => {
			if (!debouncedInput) {
				return { suggestions: [] };
			}
			const res = await fetch(`${API_URL}`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"X-Goog-Api-Key": API_KEY,
					"X-Goog-FieldMask": "*",
				},
				body: JSON.stringify({
					input: debouncedInput,
				}),
			});
			const { suggestions = [] } = await res.json();
			return suggestions;
		},
		enabled: queryEnabled,
		staleTime: ONE_MINUTE,
		retry: false,
	});

	return {
		suggestions: query.data?.map(({ placePrediction }) => predictionToSuggestion(placePrediction)) ?? [],
		isLoading: query.isLoading,
		isError: query.isError,
		error: query.error,
		refetch: query.refetch,
	};
}
